import { IPosition } from '../../types/common.types';

export type Mask = boolean[][];

const CARDINALS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

export function emptyMask(width: number, height: number): Mask {
  const mask: Mask = [];
  for (let y = 0; y < height; y++) {
    mask[y] = new Array<boolean>(width).fill(false);
  }
  return mask;
}

export function maskCells(mask: Mask): IPosition[] {
  const out: IPosition[] = [];
  for (let y = 0; y < mask.length; y++) {
    for (let x = 0; x < mask[y].length; x++) {
      if (mask[y][x]) {
        out.push({ x, y });
      }
    }
  }
  return out;
}

/**
 * Returns a copy keeping only the largest 4-connected `true` component. This is
 * the guarantee every non-convex generator relies on: union/erosion can split a
 * shape, and a disconnected room interior would strand cells the corridors
 * never reach.
 */
export function keepLargestComponent(mask: Mask): Mask {
  const height = mask.length;
  const width = mask[0]?.length ?? 0;
  const seen = emptyMask(width, height);
  let best: IPosition[] = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!mask[y][x] || seen[y][x]) {
        continue;
      }
      const comp: IPosition[] = [];
      const stack: IPosition[] = [{ x, y }];
      seen[y][x] = true;
      while (stack.length > 0) {
        const c = stack.pop() as IPosition;
        comp.push(c);
        for (const [dx, dy] of CARDINALS) {
          const nx = c.x + dx;
          const ny = c.y + dy;
          if (
            nx >= 0 &&
            ny >= 0 &&
            nx < width &&
            ny < height &&
            mask[ny][nx] &&
            !seen[ny][nx]
          ) {
            seen[ny][nx] = true;
            stack.push({ x: nx, y: ny });
          }
        }
      }
      if (comp.length > best.length) {
        best = comp;
      }
    }
  }

  const out = emptyMask(width, height);
  for (const c of best) {
    out[c.y][c.x] = true;
  }
  return out;
}
